import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { TabId } from '../../../components/mui/common/Tabs';
import { getStoredActiveTab, getTabKey, parseTabId, TAB_QUERY_PARAM } from '../../agendaRecord/recordPosition';

interface UseRecordTabSelectionOptions {
  agendaId: string;
  recordId: string;
  selectedTabProp?: TabId;
  fallbackTab: TabId;
}

interface UseRecordTabSelectionReturn {
  selectedTab: TabId;
  setSelectedTab: Dispatch<SetStateAction<TabId>>;
  selectedTabKey: string;
}

export const useRecordTabSelection = ({
  agendaId,
  recordId,
  selectedTabProp,
  fallbackTab,
}: UseRecordTabSelectionOptions): UseRecordTabSelectionReturn => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const identity = `${agendaId}:${recordId}`;
  const tabQueryParam = searchParams.get(TAB_QUERY_PARAM);
  const previousTabQueryParamRef = useRef(tabQueryParam);

  const resolveSelectedTab = useCallback(
    (): TabId => parseTabId(tabQueryParam) ?? getStoredActiveTab(agendaId, recordId) ?? selectedTabProp ?? fallbackTab,
    [agendaId, fallbackTab, recordId, selectedTabProp, tabQueryParam]
  );

  const [selectedTab, setSelectedTab] = useState<TabId>(resolveSelectedTab);
  const [resolvedIdentity, setResolvedIdentity] = useState(identity);

  if (resolvedIdentity !== identity) {
    setResolvedIdentity(identity);
    setSelectedTab(resolveSelectedTab());
  }

  const selectedTabKey = getTabKey(selectedTab);

  useEffect(() => {
    const queryChanged = previousTabQueryParamRef.current !== tabQueryParam;
    const queryTab = parseTabId(tabQueryParam);

    previousTabQueryParamRef.current = tabQueryParam;

    if (queryChanged && queryTab !== null && getTabKey(queryTab) !== selectedTabKey) {
      setSelectedTab(queryTab);
      return;
    }

    if (!selectedTabKey || getTabKey(queryTab) === selectedTabKey) return;

    setSearchParams(
      (params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set(TAB_QUERY_PARAM, selectedTabKey);
        return nextParams;
      },
      { replace: true, state: location.state }
    );
  }, [location.state, selectedTabKey, setSearchParams, tabQueryParam]);

  return { selectedTab, setSelectedTab, selectedTabKey };
};
